import {
  InsertDriveFileOutlined as FileIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import {
  Box,
  CardMedia,
  Stack,
  Typography,
  IconButton,
  SxProps,
  Theme,
} from '@mui/material'
import { FC, memo, useEffect, useMemo, useState } from 'react'

type FileListViewProps = {
  files: File[]
  setFiles: React.Dispatch<React.SetStateAction<File[]>>
  canRemoveFiles?: boolean
  containerSx?: SxProps<Theme>
}

const FileListView: FC<FileListViewProps> = ({
  files,
  setFiles,
  canRemoveFiles = false,
  containerSx,
}) => {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null)

  const objectURLs = useMemo(() => {
    const map = new Map<File, string>()
    files.forEach((f) => {
      if (f.type.startsWith('image/')) map.set(f, URL.createObjectURL(f))
    })
    return map
  }, [files])

  // Object URLs are external browser resources that persist until explicitly
  // revoked. useEffect cleanup is the only way to revoke them when files
  // changes or the component unmounts.
  useEffect(() => {
    return () => objectURLs.forEach((url) => URL.revokeObjectURL(url))
  }, [objectURLs])

  const handleDownload = (file: File) => {
    const url = URL.createObjectURL(file)
    const link = document.createElement('a')
    link.href = url
    link.download = file.name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleDelete = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx))
  }

  return (
    <Stack
      direction={'row'}
      flexWrap="wrap"
      justifyContent={'center'}
      gap="10px"
      sx={containerSx}
    >
      {files.map((file, idx) => (
        <Stack
          key={`${file.name}-${idx}`}
          sx={{
            position: 'relative',
            width: '100px',
            height: '100px',
            cursor: 'pointer',
          }}
          onMouseEnter={() => setHoveredIdx(idx)}
          onMouseLeave={() => setHoveredIdx(null)}
        >
          {file.type.startsWith('image/') ? (
            <CardMedia
              component={'img'}
              image={objectURLs.get(file)}
              alt={file.name}
              sx={{
                borderColor: 'divider',
                border: '1px solid',
                borderRadius: 1,
                overflow: 'hidden',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderRadius: 1,
              }}
            >
              <Stack
                justifyContent={'center'}
                alignItems={'center'}
                sx={{ overflow: 'hidden', padding: '4px' }}
              >
                <FileIcon />
                <Typography
                  variant="caption"
                  align="center"
                  sx={{
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    wordBreak: 'break-all',
                  }}
                >
                  {file.name}
                </Typography>
              </Stack>
            </Box>
          )}
          {hoveredIdx === idx && canRemoveFiles && (
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                top: -7,
                right: 4,
                zIndex: 2,
                bgcolor: 'background.paper',
                '&:hover': { bgcolor: 'grey.200' },
              }}
              onClick={(e) => {
                e.stopPropagation()
                handleDelete(idx)
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
          {hoveredIdx === idx && (
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                bgcolor: 'background.paper',
                '&:hover': { bgcolor: 'grey.200' },
              }}
              onClick={(e) => {
                e.stopPropagation()
                handleDownload(file)
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      ))}
    </Stack>
  )
}

export default memo(FileListView)
